'use client';

import { useQueryClient } from '@tanstack/react-query';
import Link from 'next/link';
import React, { useMemo } from 'react';
import LikeButton from 'src/components/_common/Button/new/LikeButton';
import NextImage from 'src/components/_common/NextImage';
import { USE_MAIN_QUERY_KEY } from 'src/store/server/main/new/main.query';
import { useSetOriginalsFavorMutation } from 'src/store/server/originals/originals.query';
import { momentTimezone } from 'src/utils/time';

import OriginalsCardStyled from './OriginalsCard.styled';

export interface OriginalsCardProps {
  originalsIdx: number;
  title: string;
  subTitle: string;
  image: string;
  startDateTime: number;
  endDateTime: number;
  linkUrl: string;
  blankYN: 'Y' | 'N';
  myFavorite: 'Y' | 'N';
}

const OriginalsCard = ({
  originalsIdx,
  title,
  subTitle,
  image,
  startDateTime,
  endDateTime,
  linkUrl,
  blankYN,
  myFavorite,
}: OriginalsCardProps) => {
  const queryClient = useQueryClient();
  const { mutate: setOriginalsFavorMutate } = useSetOriginalsFavorMutation({
    onSuccess: () => {
      queryClient.invalidateQueries([USE_MAIN_QUERY_KEY.getMyPickBrandList]);
    },
    onError: () => {
      // 에러 상황
    },
  });

  const currentTime = momentTimezone().unix();

  const formattedDate = useMemo(() => {
    const startDate = momentTimezone(startDateTime).format('YYYY년 M월 D일');
    const endDate = momentTimezone(endDateTime).format('YYYY년 M월 D일');
    return `${startDate}부터 ${endDate}까지`;
  }, [startDateTime, endDateTime]);

  const getOriginalsStatus = () => {
    if (currentTime > endDateTime) {
      return '완료';
    }
    if (currentTime >= startDateTime && currentTime <= endDateTime) {
      return '진행중';
    }
    return '예정'; // 시작 전 상태도 고려
  };

  const handleToggleLike = async () => {
    const favorYN: 'Y' | 'N' = myFavorite === 'Y' ? 'N' : 'Y';

    setOriginalsFavorMutate({
      originalsIdx,
      favorYN,
    });
  };

  return (
    <OriginalsCardStyled.Container>
      <Link
        href={linkUrl}
        {...(blankYN && {
          target: '_blank',
          rel: 'noopener noreferrer',
        })}
        passHref
        legacyBehavior
      >
        <OriginalsCardStyled.ImageWrapper>
          <NextImage
            src={image}
            /**
             * 이미지 내부에 텍스트가 포함되어 있어
             * 1. 스크린리더 사용자를 위한 접근성
             * 2. 검색엔진 최적화(SEO)
             * 를 위해 제목, 부제목, 기간 등 모든 정보를 대체 텍스트로 제공
             */
            alt={`[오리지널스] ${subTitle} ${title} ${formattedDate}`}
            width={320}
            height={180}
            style={{
              width: '100%',
              height: 'auto',
              objectFit: 'cover',
            }}
            loading="eager"
            priority
          />
        </OriginalsCardStyled.ImageWrapper>
      </Link>
      <OriginalsCardStyled.StatusBadge role="status" aria-label="이벤트 상태">
        {getOriginalsStatus()}
      </OriginalsCardStyled.StatusBadge>
      <OriginalsCardStyled.LikeButtonWrapper>
        <LikeButton isActive={myFavorite === 'Y'} isAnimate={myFavorite === 'Y'} onClick={handleToggleLike} />
      </OriginalsCardStyled.LikeButtonWrapper>
    </OriginalsCardStyled.Container>
  );
};

export default OriginalsCard;
